/* likeness.js — real sculpted likeness heads on AGENTSEE's characters.
 *
 * A seat wears a scanned bust instead of its avatar's head. The point of this module is
 * that it must not look like a grey stone ball dropped on a suit, so three quarters of
 * the code here is measurement and material, not loading.
 *
 * WHAT IT DOES
 *   1. loads /assets/likeness/<file>, caches the prepared geometry per file, clones per use
 *   2. auto-fits: bbox-derived scale against real head anthropometry, and a crown anchor
 *      measured off the avatar's own skeleton bind pose (pose- and frame-independent)
 *   3. hides the avatar's own head meshes so two heads never coexist
 *   4. gives the bust one of three tuned treatments — flesh / bronze / marble
 *   5. repairs missing or degenerate normals, welds where that helps
 *   6. decimates anything over the triangle budget (see TRI_BUDGET)
 *   7. is entirely static: no per-frame hook, no uTime, no world-transform assumptions.
 *      It is a child of `headInner`, so the host's `followHead` carries it for free.
 *
 * USAGE (host side, inside makeRig's skinned branch, next to the ATG helm block)
 *
 *     import { initLikeness, attachLikeness, LIKENESS } from './likeness.js';
 *     initLikeness(THREE);                                  // once, at module init
 *     ...
 *     if (LIKENESS[head]) attachLikeness({ file: LIKENESS[head], headInner, character: root })
 *       .then(l => { refs.likeness = l; }).catch(()=>{});
 *
 * SOURCES / LICENCES for the meshes: public/assets/likeness/SOURCES.md. Two of the six
 * (jobs, alexander-bronze-alt) are NC/ND — private build only.
 */

import * as THREE_NS from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { mergeVertices } from 'three/addons/utils/BufferGeometryUtils.js';
import { SimplifyModifier } from 'three/addons/modifiers/SimplifyModifier.js';

/* ------------------------------------------------------------------ *
 *  the cast                                                           *
 * ------------------------------------------------------------------ */

export const LIKENESS = {
  jobs:        'jobs.glb',
  shakespeare: 'shakespeare.glb',
  atg:         'alexander.glb',
  efficacy:    'obama.glb',
};

/* the alternates that shipped alongside — different sculptures of the same subjects */
export const LIKENESS_ALT = {
  atgBronze:    'alexander-bronze-alt.glb',   // the Met bronze — reads as metal, not marble
  efficacyScan: 'obama-facescan-alt.glb',     // photogrammetry, but a face shell on a slab
};

/* Per-subject default treatment.
 *   Jobs and Obama are living/recent people who sit in a room of photographed humans —
 *   a stone head next to a photoreal colleague reads as a bug, so they get skin.
 *   Shakespeare and Alexander are sculpture in the first place: the LiDAR scan is of a
 *   plaster prop bust and the Wenman scan is of Hellenistic marble in the British Museum.
 *   Rendering those as flesh would be the wrong lie; the bust IS the likeness.
 *   alexander-bronze-alt is a scan of the Met's bronze head, so it gets bronze. */
const TREATMENT = {
  'jobs.glb':                 'flesh',
  'obama.glb':                'flesh',
  'obama-facescan-alt.glb':   'flesh',
  'shakespeare.glb':          'marble',
  'alexander.glb':            'marble',
  'alexander-bronze-alt.glb': 'bronze',
};
const TREATMENTS = ['flesh', 'bronze', 'marble'];

/* Default skin tones, matched to the host's own FACES palette where one exists.
 * FACES.jobs.skin is '#c9a483' in main.js — reuse it verbatim so the bust and any
 * remaining painted geometry agree. There is no FACES entry for Obama (he is a
 * portrait-only seat), so this is a plain mid-brown chosen to sit in the same light. */
const SKIN = {
  'jobs.glb':               0xc9a483,
  'obama.glb':              0x8c5f42,
  'obama-facescan-alt.glb': 0x8c5f42,
};
const SKIN_DEFAULT = 0xc0987a;

/* Which meshes are "the avatar's own head". Tested against BOTH the mesh name and the
 * material name(s), because the two avatar families hide their identity in different places:
 *
 *   RPM (public/assets/rpm/a*.glb) — one mesh per part, so the MESH name is the handle:
 *     EyeLeft · EyeRight · Wolf3D_Head · Wolf3D_Teeth · Wolf3D_Hair · Wolf3D_Beard · Wolf3D_Glasses
 *     (Wolf3D_Body is the neck/arms/torso skin — never hide it.)
 *     Note main.js rebuilds RPM materials via mkPhys() without copying .name, so by the time
 *     we run, RPM material names are empty. Mesh names survive. Hence the mesh-name rules.
 *
 *   Rocketbox (public/assets/humans/*.glb) — ONE glTF mesh "Mesh" with several primitives,
 *     which GLTFLoader turns into meshes named Mesh, Mesh_1, Mesh_2… The stable handle is the
 *     MATERIAL name: <id>_body, <id>_head, <id>_opacity (hair/lash alpha cards), <id>_glasses.
 *     e.g. andre → m016_body, m016_head. main.js leaves these materials untouched, so the
 *     names are still there. `Mesh_1` is the head primitive in all eight files, but the
 *     material rule is the one that will not rot.
 */
export const LIKENESS_HIDE = {
  rpm: [
    /^Wolf3D_(Head|Hair|Beard|Teeth|Glasses|Headwear|Facewear)$/i,
    /^Eye(Left|Right)$/i,
  ],
  rocketbox: [
    /_head$/i,
    /_opacity$/i,
    /_glasses$/i,
  ],
};
LIKENESS_HIDE.any = LIKENESS_HIDE.rpm.concat(LIKENESS_HIDE.rocketbox);

/* ------------------------------------------------------------------ *
 *  the numbers, and where they come from                              *
 * ------------------------------------------------------------------ */

/* Triangle budget per bust: 55,000.
 * The shipped six are pre-decimated in Blender to 40,000–45,000 tris (SOURCES.md §3), so
 * NONE of them trip this at runtime — the budget is insurance against a heavier drop-in.
 * It sits above the shipped max because SimplifyModifier is a synchronous main-thread
 * progressive-mesh collapse; firing it on every load would stall the frame for no gain.
 * Decimation runs once per FILE (on the cached geometry), never per seat. */
const TRI_BUDGET = 55000;

/* Anthropometry. All of it is expressed in HEADINNER-LOCAL UNITS, which for this app are
 * "the metres of the figure itself": every character GLB is authored at 1.75–1.87 units tall
 * and the seat's overall size comes from root.scale (1.0 for most, 1.12 for ATG, 1.06 for
 * Ben). Fitting in local units rather than world metres is what makes a 1.12× seat get a
 * 1.12× head instead of a normal head on an oversized body. For an unscaled seat, one local
 * unit is one metre. */
const STATURE_M        = 1.75;                          // the figure these seats are built to
const HEADS_PER_STATURE = 7.5;                          // adult canon
const HEAD_H_M         = STATURE_M / HEADS_PER_STATURE; // 0.2333 — menton to vertex
const NECK_STUB_M      = 0.035;                         // the collar-to-chin stub these busts keep
const TARGET_BUST_M    = HEAD_H_M + NECK_STUB_M;        // 0.2683 — target total bust height

/* Widest the cranium band (top 35% of the bust) may be after fitting. Adult head breadth is
 * ~0.152; hair, a wig or a helmet crest legitimately pushes past that, so this is a soft
 * ceiling that only trims the genuinely oversized (Shakespeare's hair, the Obama face slab). */
const HEAD_W_MAX_M = 0.195;

/* Fallback crown height above the Head bone, used only when the avatar cannot be measured.
 * Measured, not guessed: rpm/a4.glb has Wolf3D_Head max Y 1.8331 and its Head bone at
 * 1.6482 → 0.1849 m. */
const CROWN_FALLBACK_M = 0.185;

/* Where the cranium band's Z centre should sit relative to the neck-base pivot. The four
 * well-behaved shipped files land at −0.006 … +0.014, so this is their middle. Corrections
 * are dead-banded (below) so a file already inside the band is never nudged. */
const CRANIUM_Z_TARGET_M = 0.004;
const DEADBAND_X_M = 0.010;
const DEADBAND_Z_M = 0.020;

/* Sanity window for a measured crown height, in metres. Outside it we distrust the
 * measurement and fall back. */
const CROWN_MIN_M = 0.08, CROWN_MAX_M = 0.45;

const ASSET_DIR = '/assets/likeness/';

/* ------------------------------------------------------------------ *
 *  init                                                               *
 * ------------------------------------------------------------------ */

let T = THREE_NS;          // the host's THREE, so we never end up with two copies
let loader = null;
let simplifier = null;
const GEO_CACHE = new Map();   // file -> Promise<BufferGeometry>  (prepared, shared, never disposed)

/**
 * Hand the module the host's THREE namespace. Optional — the module imports its own and
 * Vite dedupes — but calling it guarantees one instance even in odd bundling setups.
 * @param {object} THREE
 * @param {object} [opts] { loader } — pass the host's GLTFLoader to reuse its manager/decoders.
 */
export function initLikeness(THREE, opts) {
  if (THREE) T = THREE;
  if (opts && opts.loader) loader = opts.loader;
  return { LIKENESS, LIKENESS_ALT, LIKENESS_HIDE, TRI_BUDGET };
}

function getLoader() {
  if (!loader) loader = new GLTFLoader();
  return loader;
}

/* ------------------------------------------------------------------ *
 *  geometry: load → repair → weld → decimate → cavity                 *
 * ------------------------------------------------------------------ */

function loadGeometry(file) {
  if (GEO_CACHE.has(file)) return GEO_CACHE.get(file);

  const p = new Promise((res, rej) => {
    getLoader().load(ASSET_DIR + file, g => {
      let found = null;
      g.scene.traverse(o => { if (!found && o.isMesh && o.geometry) found = o; });
      if (!found) { rej(new Error('likeness: no mesh in ' + file)); return; }
      try { res(prepare(found.geometry, file)); }
      catch (e) { rej(e); }
    }, undefined, err => rej(err || new Error('likeness: failed to load ' + file)));
  });

  GEO_CACHE.set(file, p);
  p.catch(() => GEO_CACHE.delete(file));   // a failed load must not poison the cache
  return p;
}

function triCount(geo) {
  const pos = geo.getAttribute('position');
  if (!pos) return 0;
  return (geo.index ? geo.index.count : pos.count) / 3;
}

/* Are the supplied normals usable? Photogrammetry exports sometimes ship none, or ship a
 * sea of zero-length ones after a bad weld. Sample rather than scan 22k verts. */
function normalsAreBad(geo) {
  const n = geo.getAttribute('normal');
  if (!n) return true;
  const count = n.count;
  if (!count) return true;
  const step = Math.max(1, Math.floor(count / 2048));
  let bad = 0, seen = 0;
  for (let i = 0; i < count; i += step) {
    const x = n.getX(i), y = n.getY(i), z = n.getZ(i);
    const len = Math.sqrt(x * x + y * y + z * z);
    if (!(len > 0.5) || !Number.isFinite(len)) bad++;
    seen++;
  }
  return seen === 0 || bad / seen > 0.02;
}

function prepare(src, file) {
  let geo = src.clone();
  geo.morphAttributes = {};
  // nothing here is skinned, and stray attributes only get in the decimator's way
  for (const k of Object.keys(geo.attributes)) {
    if (k !== 'position' && k !== 'normal' && k !== 'uv') geo.deleteAttribute(k);
  }

  /* 5. normals — compute them if missing, and weld where that helps.
   *    Welding with a normal attribute present only merges vertices identical in position
   *    AND normal, which welds nothing on a split-normal scan. So when the normals are
   *    unusable we drop them first, weld on position, then recompute — that actually
   *    closes the seams. When they are fine we leave the mesh alone; the shipped six all
   *    carry clean Blender-smoothed normals (verified: 0 degenerate normals in all six). */
  const hadBadNormals = normalsAreBad(geo);
  if (hadBadNormals) {
    geo.deleteAttribute('normal');
    try { geo = mergeVertices(geo, 1e-4); } catch (e) { /* non-manifold: carry on unwelded */ }
    geo.computeVertexNormals();
  } else if (!geo.index) {
    // indexing is free to obtain and both the decimator and the cavity smoother want it
    try { geo = mergeVertices(geo, 1e-4); } catch (e) { /* fine unindexed */ }
  }

  /* 6. decimate over budget */
  const before = triCount(geo);
  if (before > TRI_BUDGET) {
    const decimated = decimate(geo, TRI_BUDGET);
    if (decimated) geo = decimated;
  }

  geo.computeBoundingBox();
  geo.computeBoundingSphere();
  addCavityAttribute(geo);          // after decimation: SimplifyModifier strips custom attrs

  geo.userData.likeness = {
    file,
    tris: triCount(geo),
    trisBefore: before,
    decimated: before > TRI_BUDGET,
    normalsRepaired: hadBadNormals,
    band: craniumBand(geo),
  };
  return geo;
}

function decimate(geo, targetTris) {
  try {
    if (!simplifier) simplifier = new SimplifyModifier();
    const verts = geo.getAttribute('position').count;
    const keepRatio = Math.min(1, targetTris / triCount(geo));
    const remove = Math.max(0, Math.floor(verts * (1 - keepRatio)));
    if (!remove) return null;
    const out = simplifier.modify(geo, remove);
    // non-manifold scans can come back with NaN — validate before accepting
    out.computeBoundingBox();
    const b = out.getAttribute('position');
    if (!b || !b.count || !out.boundingBox || !isFiniteBox(out.boundingBox)) return null;
    if (!out.getAttribute('normal')) out.computeVertexNormals();
    return out;
  } catch (e) {
    return null;    // a bust at 45k is better than no bust
  }
}

function isFiniteBox(b) {
  return [b.min.x, b.min.y, b.min.z, b.max.x, b.max.y, b.max.z].every(Number.isFinite)
    && b.max.x > b.min.x && b.max.y > b.min.y;
}

/* Measure the cranium band — the top 35% of the bust. Used for the width guard and for
 * self-centring, because the full bbox is polluted by collars, ruffs and shoulder stubs. */
function craniumBand(geo, frac = 0.65) {
  const pos = geo.getAttribute('position');
  const box = geo.boundingBox;
  const y0 = box.min.y + (box.max.y - box.min.y) * frac;
  let xmin = Infinity, xmax = -Infinity, zmin = Infinity, zmax = -Infinity, n = 0;
  for (let i = 0; i < pos.count; i++) {
    if (pos.getY(i) < y0) continue;
    const x = pos.getX(i), z = pos.getZ(i);
    if (x < xmin) xmin = x; if (x > xmax) xmax = x;
    if (z < zmin) zmin = z; if (z > zmax) zmax = z;
    n++;
  }
  if (!n) return { w: box.max.x - box.min.x, d: box.max.z - box.min.z, cx: 0, cz: 0, n: 0 };
  return { w: xmax - xmin, d: zmax - zmin, cx: (xmax + xmin) / 2, cz: (zmax + zmin) / 2, n };
}

/* ---- per-vertex cavity ------------------------------------------------------------
 * The single thing that stops a bare scan reading as a grey ball is knowing where the
 * recesses are: eye sockets, nostrils, the line of the lips, under the jaw, behind the ear.
 * There are no AO maps here, so derive it from the geometry.
 *
 * A head is roughly star-shaped about its cranial centre, so dot(normalize(p - c), n) is
 * ~1 on protruding form (nose, brow, cheekbone) and low or negative in recesses. Crude on
 * a teapot, remarkably good on a head. Percentile-normalised so every file lands on the
 * same 0..1 range, then neighbour-averaged over the index buffer — raw, it is speckle on a
 * 22k-vertex photoscan, and speckle reads as noise rather than patina.
 *
 * Result: 1 = high point, 0 = deep recess. Every treatment uses it.
 */
function addCavityAttribute(geo) {
  const pos = geo.getAttribute('position');
  const nor = geo.getAttribute('normal');
  const count = pos.count;
  const out = new Float32Array(count);

  if (!nor || !count) { out.fill(0.5); geo.setAttribute('aCavity', new T.BufferAttribute(out, 1)); return; }

  // centre of the cranial mass, not the bbox centre — the bbox is dragged down by collars
  const box = geo.boundingBox || (geo.computeBoundingBox(), geo.boundingBox);
  const y0 = box.min.y + (box.max.y - box.min.y) * 0.45;
  let cx = 0, cy = 0, cz = 0, n = 0;
  for (let i = 0; i < count; i++) {
    if (pos.getY(i) < y0) continue;
    cx += pos.getX(i); cy += pos.getY(i); cz += pos.getZ(i); n++;
  }
  if (n < 8) { cx = (box.min.x + box.max.x) / 2; cy = (box.min.y + box.max.y) / 2; cz = (box.min.z + box.max.z) / 2; }
  else { cx /= n; cy /= n; cz /= n; }

  const raw = new Float32Array(count);
  for (let i = 0; i < count; i++) {
    let dx = pos.getX(i) - cx, dy = pos.getY(i) - cy, dz = pos.getZ(i) - cz;
    const len = Math.hypot(dx, dy, dz) || 1e-6;
    dx /= len; dy /= len; dz /= len;
    const d = dx * nor.getX(i) + dy * nor.getY(i) + dz * nor.getZ(i);
    raw[i] = Number.isFinite(d) ? d : 1;
  }

  // percentile-normalise so the same shader constants suit every file
  const sample = [];
  const step = Math.max(1, Math.floor(count / 4096));
  for (let i = 0; i < count; i += step) sample.push(raw[i]);
  sample.sort((a, b) => a - b);
  const lo = sample[Math.floor(sample.length * 0.10)];
  const hi = sample[Math.floor(sample.length * 0.85)];
  const span = (hi - lo) > 1e-4 ? (hi - lo) : 1;
  for (let i = 0; i < count; i++) {
    const t = (raw[i] - lo) / span;
    out[i] = t < 0 ? 0 : t > 1 ? 1 : t;
  }

  // neighbour-average over the index buffer — 3 passes turns speckle into form
  const idx = geo.index;
  if (idx) {
    const acc = new Float32Array(count), cnt = new Float32Array(count);
    for (let pass = 0; pass < 3; pass++) {
      acc.fill(0); cnt.fill(0);
      for (let i = 0; i < idx.count; i += 3) {
        const a = idx.getX(i), b = idx.getX(i + 1), c = idx.getX(i + 2);
        acc[a] += out[b] + out[c]; cnt[a] += 2;
        acc[b] += out[a] + out[c]; cnt[b] += 2;
        acc[c] += out[a] + out[b]; cnt[c] += 2;
      }
      for (let i = 0; i < count; i++) {
        if (cnt[i]) out[i] = out[i] * 0.4 + (acc[i] / cnt[i]) * 0.6;
      }
    }
  }

  geo.setAttribute('aCavity', new T.BufferAttribute(out, 1));
}

/* ------------------------------------------------------------------ *
 *  material                                                           *
 * ------------------------------------------------------------------ */

/* A silently-missed String.replace returns the source unchanged, which would compile
 * perfectly and hand back a flat default MeshPhysicalMaterial — a grey stone ball, i.e.
 * exactly the failure this module exists to prevent. So every injection is checked. */
function mustReplace(src, needle, repl, where) {
  if (src.indexOf(needle) < 0) {
    throw new Error('likeness: shader chunk "' + needle + '" not found in ' + where +
      ' — three.js version drift, the material would silently fall back to default.');
  }
  return src.replace(needle, repl);
}

const GLSL_COMMON = /* glsl */`
varying vec3 vLkPos;
varying float vLkCav;
varying vec3 vLkWorldN;
float lkHash( vec3 p ) {
  p = fract( p * 0.3183099 + vec3( 0.11, 0.17, 0.13 ) );
  p *= 17.0;
  return fract( p.x * p.y * p.z * ( p.x + p.y + p.z ) );
}
float lkNoise( vec3 x ) {
  vec3 i = floor( x ), f = fract( x );
  f = f * f * ( 3.0 - 2.0 * f );
  return mix(
    mix( mix( lkHash( i ), lkHash( i + vec3(1,0,0) ), f.x ),
         mix( lkHash( i + vec3(0,1,0) ), lkHash( i + vec3(1,1,0) ), f.x ), f.y ),
    mix( mix( lkHash( i + vec3(0,0,1) ), lkHash( i + vec3(1,0,1) ), f.x ),
         mix( lkHash( i + vec3(0,1,1) ), lkHash( i + vec3(1,1,1) ), f.x ), f.y ), f.z );
}
float lkFbm( vec3 p ) {
  float a = 0.5, s = 0.0;
  for ( int i = 0; i < 4; i ++ ) { s += a * lkNoise( p ); p *= 2.03; a *= 0.5; }
  return s;
}
`;

const GLSL_VERT_COMMON = /* glsl */`
varying vec3 vLkPos;
varying float vLkCav;
varying vec3 vLkWorldN;
attribute float aCavity;
`;

/* Rim scatter. There is no true subsurface term in MeshPhysicalMaterial, and transmission is
 * both wrong and expensive for skin. What actually sells flesh and marble at conversation
 * distance is light bleeding at grazing angles — ears, the bridge of the nose, the edge of a
 * marble cheek. Scale it by the light the surface actually received (reflectedLight is in
 * scope at this point) so it never glows in an unlit corner. */
function rimScatter(strength) {
  return /* glsl */`
  {
    vec3 lkV = normalize( vViewPosition );
    float lkFres = pow( 1.0 - clamp( dot( normal, lkV ), 0.0, 1.0 ), 2.4 );
    vec3 lkLit = reflectedLight.indirectDiffuse + reflectedLight.directDiffuse;
    float lkAmb = dot( lkLit, vec3( 0.2126, 0.7152, 0.0722 ) );
    outgoingLight += uLkScatter * lkFres * ${strength.toFixed(3)} * lkAmb * ( 0.35 + 0.65 * vLkCav );
  }
  `;
}

function buildMaterial(treatment, opts) {
  const tone = new T.Color(opts.tone);
  let mat, uniforms;

  if (treatment === 'bronze') {
    /* Cast bronze, patinated. The tells are: it is a real metal (so metalness ~1 and the
     * environment does the work), the raised form is rubbed bright while the recesses hold
     * green-black patina, and the cast surface is matte-grained rather than polished.
     * Cavity drives the patina, noise breaks up the casting. */
    mat = new T.MeshPhysicalMaterial({
      color: new T.Color(0x9a7742),
      roughness: 0.38, metalness: 0.95, envMapIntensity: 1.9,
      clearcoat: 0.0,
    });
    uniforms = {
      uLkPatina:  { value: new T.Color(0x2f4436) },   // verdigris, dark and desaturated
      uLkRub:     { value: new T.Color(0xc9a465) },   // rubbed high points
      uLkScatter: { value: new T.Color(0x000000) },
    };
  } else if (treatment === 'marble') {
    /* Museum marble/plaster. Warm off-white — never paper white, which reads as untextured
     * default. Fine grey veining from domain-warped noise, dust and shadow in the recesses,
     * and a cool rim scatter because marble is genuinely translucent a few millimetres in. */
    mat = new T.MeshPhysicalMaterial({
      color: new T.Color(0xdfd8ca),
      roughness: 0.46, metalness: 0.0, envMapIntensity: 1.15,
      clearcoat: 0.10, clearcoatRoughness: 0.55,
      sheen: 0.22, sheenRoughness: 0.9, sheenColor: new T.Color(0xfff4e2),
    });
    uniforms = {
      uLkVein:    { value: new T.Color(0x8f8a80) },
      uLkGrime:   { value: new T.Color(0x6d6659) },
      uLkScatter: { value: new T.Color(0xf3e6d2) },
    };
  } else {
    /* Skin. These values deliberately mirror the RPM skin block in main.js (roughness .58,
     * sheen .5 / .75 at 0xffd9c6, clearcoat .08 / .85, envMapIntensity 1.5) so a bust and a
     * photographed colleague are lit by the same rules and read as the same species. What
     * the bust adds on top, because it has no albedo texture to carry it, is pore-scale
     * roughness break-up, cavity shading, and the rim scatter. */
    mat = new T.MeshPhysicalMaterial({
      color: tone,
      roughness: 0.58, metalness: 0.0, envMapIntensity: 1.5,
      clearcoat: 0.08, clearcoatRoughness: 0.85,
      sheen: 0.5, sheenRoughness: 0.75, sheenColor: new T.Color(0xffd9c6),
    });
    uniforms = {
      uLkCrease:  { value: new T.Color(0x7d4a3c) },   // blood in the creases, not black
      uLkScatter: { value: new T.Color(0xd98a6a) },   // the classic warm SSS bleed
    };
  }

  mat.userData.likenessTreatment = treatment;

  mat.onBeforeCompile = shader => {
    Object.assign(shader.uniforms, uniforms);

    /* ---- vertex: carry object-space position, the cavity term, and a WORLD normal.
       (The stock vNormal is view-space, so using it for "which way is up" would make the
       bronze patina swim as the camera orbits.) ---- */
    let vert = shader.vertexShader;
    vert = mustReplace(vert, '#include <common>',
      '#include <common>\n' + GLSL_VERT_COMMON, 'vertexShader');
    vert = mustReplace(vert, '#include <begin_vertex>',
      '#include <begin_vertex>\n' +
      '  vLkPos = position;\n' +
      '  vLkCav = aCavity;\n' +
      '  vLkWorldN = normalize( mat3( modelMatrix ) * normal );', 'vertexShader');
    shader.vertexShader = vert;

    /* ---- fragment ---- */
    let frag = shader.fragmentShader;
    const decls = Object.keys(uniforms).map(k => 'uniform vec3 ' + k + ';').join('\n');
    frag = mustReplace(frag, '#include <common>',
      '#include <common>\n' + decls + '\n' + GLSL_COMMON, 'fragmentShader');

    if (treatment === 'bronze') {
      frag = mustReplace(frag, '#include <map_fragment>', /* glsl */`
      #include <map_fragment>
      {
        float lkGrain = lkFbm( vLkPos * 210.0 );
        float lkCast  = lkFbm( vLkPos * 46.0 );
        // patina collects in the recesses and where the form faces the sky
        float lkUp    = clamp( normalize( vLkWorldN ).y * 0.5 + 0.5, 0.0, 1.0 );
        float lkPat   = clamp( ( 1.0 - vLkCav ) * 1.25 + lkUp * 0.28 + lkCast * 0.22 - 0.30, 0.0, 1.0 );
        lkPat = smoothstep( 0.18, 0.86, lkPat );
        vec3 lkMetal  = mix( diffuseColor.rgb, uLkRub, smoothstep( 0.55, 1.0, vLkCav ) * 0.75 );
        diffuseColor.rgb = mix( lkMetal, uLkPatina, lkPat * 0.82 );
        diffuseColor.rgb *= 0.90 + lkGrain * 0.20;
      }
      `, 'fragmentShader');
      frag = mustReplace(frag, '#include <roughnessmap_fragment>', /* glsl */`
      #include <roughnessmap_fragment>
      {
        float lkCast = lkFbm( vLkPos * 46.0 );
        float lkPat  = smoothstep( 0.18, 0.86, clamp( ( 1.0 - vLkCav ) * 1.25 + lkCast * 0.22 - 0.30, 0.0, 1.0 ) );
        // patina is chalky, rubbed bronze is not
        roughnessFactor = clamp( roughnessFactor * ( 0.86 + lkFbm( vLkPos * 300.0 ) * 0.32 ) + lkPat * 0.34, 0.05, 1.0 );
      }
      `, 'fragmentShader');
      frag = mustReplace(frag, '#include <metalnessmap_fragment>', /* glsl */`
      #include <metalnessmap_fragment>
      {
        float lkCast = lkFbm( vLkPos * 46.0 );
        float lkPat  = smoothstep( 0.18, 0.86, clamp( ( 1.0 - vLkCav ) * 1.25 + lkCast * 0.22 - 0.30, 0.0, 1.0 ) );
        metalnessFactor = clamp( metalnessFactor * ( 1.0 - lkPat * 0.72 ), 0.0, 1.0 );
      }
      `, 'fragmentShader');
    } else if (treatment === 'marble') {
      frag = mustReplace(frag, '#include <map_fragment>', /* glsl */`
      #include <map_fragment>
      {
        // domain-warped veining, thin and directional
        vec3 lkW = vLkPos * 22.0 + vec3( lkFbm( vLkPos * 9.0 ) * 3.2 );
        float lkVein = lkFbm( lkW );
        lkVein = smoothstep( 0.52, 0.60, lkVein ) * ( 1.0 - smoothstep( 0.66, 0.80, lkVein ) );
        diffuseColor.rgb = mix( diffuseColor.rgb, uLkVein, lkVein * 0.45 );
        // dust and shadow settle in the recesses; the high points stay clean
        float lkDirt = 1.0 - smoothstep( 0.05, 0.62, vLkCav );
        diffuseColor.rgb = mix( diffuseColor.rgb, uLkGrime, lkDirt * 0.42 );
        diffuseColor.rgb *= 0.94 + lkFbm( vLkPos * 260.0 ) * 0.12;
      }
      `, 'fragmentShader');
      frag = mustReplace(frag, '#include <roughnessmap_fragment>', /* glsl */`
      #include <roughnessmap_fragment>
      roughnessFactor = clamp( roughnessFactor * ( 0.88 + lkFbm( vLkPos * 240.0 ) * 0.26 )
        + ( 1.0 - vLkCav ) * 0.16, 0.05, 1.0 );
      `, 'fragmentShader');
      frag = mustReplace(frag, '#include <opaque_fragment>',
        rimScatter(0.55) + '\n#include <opaque_fragment>', 'fragmentShader');
    } else {
      frag = mustReplace(frag, '#include <map_fragment>', /* glsl */`
      #include <map_fragment>
      {
        // living skin is never one flat colour: broad blotch + fine grain, both subtle
        float lkBlotch = lkFbm( vLkPos * 26.0 ) - 0.5;
        float lkGrain  = lkFbm( vLkPos * 340.0 ) - 0.5;
        diffuseColor.rgb *= 1.0 + lkBlotch * 0.09 + lkGrain * 0.05;
        diffuseColor.rgb = mix( diffuseColor.rgb, diffuseColor.rgb * vec3( 1.06, 0.97, 0.94 ), clamp( lkBlotch + 0.5, 0.0, 1.0 ) * 0.5 );
        // creases hold blood, not soot
        float lkCrease = 1.0 - smoothstep( 0.02, 0.55, vLkCav );
        diffuseColor.rgb = mix( diffuseColor.rgb, uLkCrease, lkCrease * 0.34 );
      }
      `, 'fragmentShader');
      frag = mustReplace(frag, '#include <roughnessmap_fragment>', /* glsl */`
      #include <roughnessmap_fragment>
      {
        // pore-scale variation: the forehead and nose read oilier, the cheek matter
        float lkPore = lkFbm( vLkPos * 420.0 );
        float lkZone = lkFbm( vLkPos * 18.0 );
        roughnessFactor = clamp( roughnessFactor * ( 0.80 + lkPore * 0.34 + lkZone * 0.14 )
          + ( 1.0 - vLkCav ) * 0.10, 0.08, 1.0 );
      }
      `, 'fragmentShader');
      frag = mustReplace(frag, '#include <opaque_fragment>',
        rimScatter(0.85) + '\n#include <opaque_fragment>', 'fragmentShader');
    }

    shader.fragmentShader = frag;
  };

  /* Without this, three caches shader programs across materials of the same type and two
   * treatments would silently share one compiled program. */
  mat.customProgramCacheKey = () => 'likeness-' + treatment;

  return mat;
}

/* ------------------------------------------------------------------ *
 *  finding and hiding the avatar's own head                           *
 * ------------------------------------------------------------------ */

function matchesAny(obj, patterns) {
  const names = [obj.name || ''];
  const m = obj.material;
  if (Array.isArray(m)) m.forEach(x => x && x.name && names.push(x.name));
  else if (m && m.name) names.push(m.name);
  for (const p of patterns) {
    const re = p instanceof RegExp ? p : new RegExp(p, 'i');
    for (const n of names) if (n && re.test(n)) return true;
  }
  return false;
}

/* Walk up from headInner to the character. Bounded hard: without the bound this can reach
 * the scene root and hide every head on the floor. In the host's tree the answer is two
 * hops (headInner → holder → root). Pass opts.character to skip the search entirely. */
function findCharacter(headInner, override) {
  if (override) return override;
  let node = headInner.parent;
  for (let i = 0; i < 3 && node; i++, node = node.parent) {
    if (node.isScene) break;
    let has = false;
    node.traverse(o => { if (!has && o.isSkinnedMesh) has = true; });
    if (has) return node;
  }
  return null;
}

function firstSkinned(root) {
  let sk = null;
  if (root) root.traverse(o => { if (!sk && o.isSkinnedMesh && o.skeleton) sk = o; });
  return sk;
}

/* ------------------------------------------------------------------ *
 *  the fit                                                            *
 * ------------------------------------------------------------------ */

/* Measure the avatar's crown in BIND space.
 *
 * The obvious approach — Box3.setFromObject(headMesh) against bone.getWorldPosition() —
 * is wrong: for a SkinnedMesh, setFromObject returns the *bind-pose* geometry box put
 * through matrixWorld, while getWorldPosition returns the *posed* bone. A seated or
 * spine-bent avatar then fits differently from a standing one, and the fit changes as the
 * idle clip breathes.
 *
 * The skeleton carries the answer directly. GLTFLoader binds with an identity bindMatrix,
 * so geometry positions and inverse-bind matrices live in the same space; the inverse of
 * boneInverses[i] is that bone's bind transform in it. Both quantities are therefore exact,
 * pose-independent and frame-independent — no rAF retries, no staleness guard.
 *
 * We measure the union of exactly the meshes we are about to hide, not "the head mesh":
 * on rpm/a1.glb Wolf3D_Hair reaches 1.770 against Wolf3D_Head's 1.743, so the hair IS the
 * crown. The thing you hide is the thing you measure.
 *
 * Returns headInner-local units, or null if anything looks wrong.
 */
function measureCrown(headInner, hidden, character, u) {
  const sk = hidden.find(o => o.isSkinnedMesh && o.skeleton) || firstSkinned(character);
  if (!sk || !sk.skeleton || !sk.skeleton.boneInverses) return null;

  const bones = sk.skeleton.bones;
  let idx = bones.findIndex(b => /^(mixamorig[:_]?)?Head$/i.test(b.name || ''));
  if (idx < 0) idx = bones.findIndex(b => /Head$/i.test(b.name || '') && !/Top|End/i.test(b.name || ''));
  if (idx < 0) return null;

  const bindM = new T.Matrix4().copy(sk.skeleton.boneInverses[idx]).invert();
  const bonePos = new T.Vector3().setFromMatrixPosition(bindM);

  const box = new T.Box3();
  const tmp = new T.Box3();
  let used = 0;
  for (const o of hidden) {
    if (!o.geometry) continue;
    if (o.isSkinnedMesh && o.skeleton !== sk.skeleton) continue;
    if (!o.geometry.boundingBox) o.geometry.computeBoundingBox();
    if (!o.geometry.boundingBox || !isFiniteBox(o.geometry.boundingBox)) continue;
    tmp.copy(o.geometry.boundingBox);
    if (o.isSkinnedMesh && o.bindMatrix) tmp.applyMatrix4(o.bindMatrix);
    box.union(tmp); used++;
  }
  if (!used) {
    if (!sk.geometry.boundingBox) sk.geometry.computeBoundingBox();
    if (!sk.geometry.boundingBox) return null;
    box.copy(sk.geometry.boundingBox);
    if (sk.bindMatrix) box.applyMatrix4(sk.bindMatrix);
  }
  if (!isFiniteBox(box)) return null;

  /* bind units → headInner-local units. bindMatrix is identity from GLTFLoader, so the only
   * factors are the mesh's current world scale and headInner's. Scale is immune to
   * followHead, which writes position and quaternion only — safe to read at attach time,
   * before the follower has ever run. */
  const meshScale = new T.Vector3();
  sk.updateWorldMatrix(true, false);
  sk.getWorldScale(meshScale);
  const bindScale = new T.Vector3().setFromMatrixScale(sk.bindMatrix || new T.Matrix4());
  const f = ((meshScale.x || 1) / (bindScale.x || 1)) / (u || 1);

  const crownLocal = (box.max.y - bonePos.y) * f;
  if (!Number.isFinite(crownLocal) || crownLocal < CROWN_MIN_M || crownLocal > CROWN_MAX_M) return null;

  return {
    crownLocal,
    headHeightLocal: (box.max.y - box.min.y) * f,
    bone: bones[idx].name,
    meshes: used,
  };
}

/* corrections are dead-banded: a file already inside the band is never nudged */
function deadband(v, d) {
  if (!Number.isFinite(v)) return 0;
  if (Math.abs(v) <= d) return 0;
  return v - Math.sign(v) * d;
}

function vec3of(v) {
  if (!v) return { x: 0, y: 0, z: 0 };
  if (Array.isArray(v)) return { x: v[0] || 0, y: v[1] || 0, z: v[2] || 0 };
  return { x: v.x || 0, y: v.y || 0, z: v.z || 0 };
}

/* ------------------------------------------------------------------ *
 *  attach                                                             *
 * ------------------------------------------------------------------ */

/**
 * Put a sculpted likeness head onto a character.
 *
 * @param {object} opts
 * @param {string}   opts.file        filename in /assets/likeness/ — use LIKENESS.<seat>
 * @param {Object3D} opts.headInner   the host's per-character glance group (see makeRig)
 * @param {string}  [opts.kind]       'flesh' | 'bronze' | 'marble'. Anything else (including
 *                                    a rig kind like 'atg') falls back to the per-file default.
 * @param {number|string} [opts.skinTone]  flesh tone; defaults per subject
 * @param {number}  [opts.scale=1]    multiplier on the derived fit — a trim, not the fit
 * @param {Array|Vector3} [opts.offset]  extra offset in headInner-LOCAL units (≈ metres for an
 *                                    unscaled seat), applied after the fit
 * @param {Array|Vector3} [opts.rotation] extra rotation in radians (busts already face +Z)
 * @param {Array<RegExp>} [opts.hideMeshes]  defaults to LIKENESS_HIDE.any
 * @param {Object3D} [opts.character] the character root; found by bounded walk-up if omitted
 * @param {boolean} [opts.hideExisting=true]  hide whatever was already parented to headInner
 *                                    (this is how the ATG bronze helm gets out of the way)
 * @returns {Promise<{group: Group, dispose: Function, info: object}>}
 */
export function attachLikeness(opts) {
  const o = opts || {};
  const headInner = o.headInner;
  const file = o.file;

  if (!headInner || !headInner.isObject3D) return Promise.reject(new Error('likeness: opts.headInner required'));
  if (!file) return Promise.reject(new Error('likeness: opts.file required'));

  return loadGeometry(file).then(geo => {
    const meta = geo.userData.likeness || {};
    const treatment = TREATMENTS.indexOf(o.kind) >= 0 ? o.kind : (TREATMENT[file] || 'flesh');
    const tone = o.skinTone !== undefined && o.skinTone !== null
      ? o.skinTone : (SKIN[file] !== undefined ? SKIN[file] : SKIN_DEFAULT);

    /* --- 3. hide the avatar's own head. Only now, after a successful load: a failed fetch
       must never leave a headless avatar on the floor. --- */
    const patterns = o.hideMeshes && o.hideMeshes.length ? o.hideMeshes : LIKENESS_HIDE.any;
    const character = findCharacter(headInner, o.character);
    const hidden = [];
    const restore = [];
    if (character) {
      const refSkel = (firstSkinned(character) || {}).skeleton || null;
      character.traverse(obj => {
        if (!obj.isMesh) return;
        if (refSkel && obj.isSkinnedMesh && obj.skeleton !== refSkel) return;  // never touch a neighbour
        if (!matchesAny(obj, patterns)) return;
        hidden.push(obj);
        restore.push({ obj, visible: obj.visible });
        obj.visible = false;
      });
    }

    /* whatever the host already parented to headInner — the ATG bronze helm is the live
       case — would intersect a full bust, so it goes too, reversibly. */
    const preexisting = [];
    if (o.hideExisting !== false) {
      for (const c of headInner.children.slice()) {
        preexisting.push({ obj: c, visible: c.visible });
        c.visible = false;
      }
    }

    /* --- 2. the fit. Everything below is in headInner-local units. --- */
    headInner.updateWorldMatrix(true, false);
    const uVec = new T.Vector3();
    headInner.getWorldScale(uVec);
    const u = (Number.isFinite(uVec.x) && Math.abs(uVec.x) > 1e-5) ? uVec.x : 1;  // world metres per local unit

    const box = geo.boundingBox;
    const bustH = box.max.y - box.min.y;          // asset metres
    const band = meta.band || craniumBand(geo);

    /* scale: bbox height against the anthropometric target, then a soft ceiling on the
       cranium band's width so a wig or a face-slab cannot blow out sideways */
    let s = TARGET_BUST_M / (bustH > 1e-5 ? bustH : TARGET_BUST_M);
    const fittedTopW = band.w * s;
    if (fittedTopW > HEAD_W_MAX_M) s *= HEAD_W_MAX_M / fittedTopW;
    s *= Number.isFinite(o.scale) ? o.scale : 1;

    const measured = measureCrown(headInner, hidden, character, u);
    const crownLocal = measured ? measured.crownLocal : CROWN_FALLBACK_M;

    // X: centre the cranium band on the neck axis. Z: nudge only the genuine outliers.
    const dxA = -deadband(band.cx, DEADBAND_X_M);
    const dzA = -deadband(band.cz - CRANIUM_Z_TARGET_M, DEADBAND_Z_M);
    const off = vec3of(o.offset);

    const pos = {
      x: dxA * s + off.x,
      y: (crownLocal - bustH * s) + off.y,   // crown of the bust lands on the crown of the avatar
      z: dzA * s + off.z,
    };

    /* --- build --- */
    const mat = buildMaterial(treatment, { tone });
    const mesh = new T.Mesh(geo, mat);      // geometry is shared/cached; only the material is ours
    mesh.name = 'likeness:' + file;
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    mesh.frustumCulled = false;             // it is re-posed every frame by the host's follower

    const group = new T.Group();
    group.name = 'likenessHead';
    group.add(mesh);

    /* Everything below is expressed in headInner's LOCAL units, which is the only space this
       module ever assumes. followHead rewrites headInner's ancestors every frame; because we
       are a plain child with a static local transform, we simply come along. No world matrix
       is cached, no per-frame hook is installed. */
    group.scale.setScalar(s);
    group.position.set(pos.x, pos.y, pos.z);
    const rot = vec3of(o.rotation);
    group.rotation.set(rot.x, rot.y, rot.z);

    headInner.add(group);

    /* --- teardown --- */
    let disposed = false;
    const dispose = () => {
      if (disposed) return;
      disposed = true;
      if (group.parent) group.parent.remove(group);
      mat.dispose();                        // ours alone
      // geo is cached and may be shared with another seat — never dispose it here
      for (const r of restore) r.obj.visible = r.visible;
      for (const r of preexisting) r.obj.visible = r.visible;
    };

    return {
      group,
      dispose,
      info: {
        file, treatment,
        tris: meta.tris,
        decimated: !!meta.decimated,
        normalsRepaired: !!meta.normalsRepaired,
        scale: s,                            // local units per asset metre; what went on the group
        localUnitInMetres: u,                // multiply the locals below by this for world metres
        bustHeightLocal: bustH * s,
        craniumWidthLocal: band.w * s,
        crownLocal,
        crownSource: measured ? ('bind:' + measured.bone + ' (' + measured.meshes + ' meshes)') : 'fallback',
        position: pos,
        hidden: hidden.map(h => h.name || (h.material && h.material.name) || '?'),
        hiddenPreexisting: preexisting.length,
      },
    };
  });
}

/** Drop the cached geometry for one file, or all of them. Only for hot-reload / teardown —
 *  live busts share these geometries, so do not call it while any are on screen. */
export function clearLikenessCache(file) {
  const kill = g => { try { g.dispose(); } catch (e) {} };
  if (file) {
    const p = GEO_CACHE.get(file);
    if (p) p.then(kill, () => {});
    GEO_CACHE.delete(file);
    return;
  }
  for (const p of GEO_CACHE.values()) p.then(kill, () => {});
  GEO_CACHE.clear();
}

export default { initLikeness, attachLikeness, clearLikenessCache, LIKENESS, LIKENESS_ALT, LIKENESS_HIDE };
